import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Cat, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
    } else {
      toast.success("Welcome back");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-full bg-primary-foreground/15 flex items-center justify-center">
            <Cat className="h-5 w-5" />
          </div>
          <span className="font-serif text-2xl">AnnieHealth</span>
        </div>
        <div>
          <h1 className="font-serif text-5xl leading-tight mb-4">A gentle daily record of how you feel.</h1>
          <p className="text-primary-foreground/70 text-lg max-w-md">
            Track wellbeing with simple 1&ndash;10 daily scores, add notes on what mattered,
            and watch progress unfold across weeks, months and years.
          </p>
        </div>
        <div className="text-primary-foreground/50 text-sm">Private &middot; Caring &middot; Consistent</div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <Card className="w-full max-w-md p-8 rounded-2xl border-border/70 shadow-sm animate-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center">
              <Cat className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-serif text-xl">AnnieHealth</span>
          </div>
          <h2 className="font-serif text-3xl mb-1">Welcome back</h2>
          <p className="text-muted-foreground text-sm mb-6">Sign in to continue to your journal.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required className="rounded-xl" placeholder="you@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="login-password-input" value={password}
                onChange={(e) => setPassword(e.target.value)} required className="rounded-xl" placeholder="••••••••" />
            </div>
            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" data-testid="forgot-password-link"
                className="text-sm text-primary hover:underline">Forgot password?</Link>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-xl" data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
